import axios from "axios";
import { useContext, useState } from "react";
import AuthContext from "../store/auth-contex";
import { globalLogout } from "../helpers/logout";

/**
 * Custom methods sent as POST with X-HTTP-Method-Override, because some antivirus HTTPS
 * scanners (e.g. Avast Web Shield) reject unknown methods. The backend (Symfony Request)
 * resolves the override back to the original method.
 */
const OVERRIDDEN_METHODS = ["LIST", "FETCH"];

class ApiService {
    /** The currently logged-in user. */
    user: ?{ access_token: string };

    /** Prepare the service. */
    constructor(user) {
        this.user = user;
    }

    /**
     * Execute the API call
     *
     * @param {"LIST","GET"|"PUT"|"POST"|"DELETE"} method The HTTP method to use.
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */

    _execute(method, path, payload = null) {
        const isOverridden = OVERRIDDEN_METHODS.includes(method);

        return new Promise((resolve, reject) => {
            axios({
                method: isOverridden ? "POST" : method,
                url: localStorage.getItem("api") + path.replace(/^\//, ""),
                headers: {
                    Authorization: `Bearer ${this.user?.access_token}`,
                    "validation-api-key": localStorage.getItem("api").replace(/^https?:\/\//, ""),
                    "X-App-Type": "web",
                    "X-App-Version": process.env.REACT_APP_VERSION ?? "localhost",
                    ...(isOverridden && { "X-HTTP-Method-Override": method }),
                },
                data: payload,
            })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    if (error?.response?.status === 401 || error?.status === 401) {
                        globalLogout();
                    }
                    reject(error);
                });
        });
    }

    /**
     * Execute the get API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     *
     * @return {Promise<APIResponse>}
     */
    get(path) {
        return this._execute("GET", path);
    }

    /**
     * Execute the pet API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */
    put(path, payload = null) {
        return this._execute("PUT", path, payload);
    }

    /**
     * Execute the post API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */
    fetch(path, payload) {
        return this._execute("FETCH", path, payload);
    }

    /**
     * Execute the post API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */
    post(path, payload) {
        return this._execute("POST", path, payload);
    }

    /**
     * Execute the list API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */
    list(path, payload) {
        return this._execute("LIST", path, payload);
    }

    /**
     * Execute the put API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */
    put(path, payload) {
        return this._execute("PUT", path, payload);
    }

    /**
     * Execute the delete API call
     *
     * @param {string} path The path to the API, without the domain and API version suffix.
     * @param {?{}} payload The payload to send in the request.
     *
     * @return {Promise<APIResponse>}
     */
    delete(path, payload) {
        return this._execute("DELETE", path, payload);
    }

    /**
     * Allow to change user data
     *
     * @param {array} userData Structure recived from AuthContext.user.
     */
    userDataUpdate(userData) {
        return (this.user = userData);
    }
}

/**
 * Get the reference to the API service.
 */
const useAPI = () => {
    const { user } = useContext(AuthContext);
    return useState(new ApiService(user))[0];
};

export default useAPI;
